import api from '../../../lib/api'
import type {
  Lead, LeadCreate, LeadStatus, BulkUploadResult, WorkloadItem,
  CallInitiateResponse, CallStatusResponse, Quote, MappingTemplate, DuplicateCheckResponse,
  TelecallerWeight, ReallocationSuggestion, QuoteExtractionResult, QuotePreviewMessage,
  PreviousPolicy,
} from '../../../types'
import { LeadSource, CallSource } from '../../../types'

export const leadService = {
  async list(params?: {
    status?: LeadStatus
    status_group?: 'active' | 'closed'
    assigned_to?: number
    unassigned?: boolean
    source?: LeadSource
    search?: string
    due_within_days?: number
    sort_by?: 'closed_date'
    min_priority?: number
    page?: number
    page_size?: number
  }) {
    const res = await api.get('/leads', { params })
    return res.data
  },

  async getMyQueue() {
    const res = await api.get('/leads/my-queue', { params: { page_size: 500 } })
    return res.data.items as Lead[]
  },

  async getById(id: number) {
    const res = await api.get(`/leads/${id}`)
    return res.data as Lead
  },

  async getPreviousPolicy(id: number) {
    const res = await api.get(`/leads/${id}/previous-policy`)
    return res.data as PreviousPolicy[]
  },

  async create(data: LeadCreate) {
    const res = await api.post('/leads', data)
    return res.data as Lead
  },

  /**
   * Upload a CSV file directly to the backend (multipart/form-data).
   * Preferred over bulkUpload for large files and correct column aliasing.
   */
  async uploadCsv(
    file: File,
    dedupMode: 'skip' | 'update' | 'create' = 'skip',
    defaultSource: LeadSource = LeadSource.OEM_DUMP,
    headerRow: number = 0,
  ) {
    const form = new FormData()
    form.append('file', file)
    const res = await api.post('/leads/upload-csv', form, {
      params: { dedup_mode: dedupMode, default_source: defaultSource, header_row: headerRow },
      headers: { 'Content-Type': undefined },
    })
    return res.data as BulkUploadResult
  },

  /**
   * Legacy JSON bulk upload (still useful for programmatic use).
   */
  async bulkUpload(leads: Record<string, unknown>[], dedup_mode = 'skip', date_format: 'auto' | 'DMY' | 'MDY' = 'auto') {
    const res = await api.post('/leads/bulk', { leads, dedup_mode, date_format })
    return res.data as BulkUploadResult
  },

  // ── Duplicate pre-flight check ────────────────────────────────────────────

  async checkDuplicates(rows: Array<{
    row_index: number
    mobile: string
    vehicle_reg_no?: string
    customer_name?: string
    policy_number?: string
  }>): Promise<DuplicateCheckResponse> {
    const res = await api.post('/leads/check-duplicates', { rows })
    return res.data
  },

  // ── Column mapping templates ───────────────────────────────────────────────

  async listMappingTemplates(): Promise<MappingTemplate[]> {
    const res = await api.get('/leads/mapping-templates')
    return res.data
  },

  async saveMappingTemplate(name: string, mapping: Record<string, string>): Promise<MappingTemplate> {
    const res = await api.post('/leads/mapping-templates', { name, mapping })
    return res.data
  },

  async deleteMappingTemplate(id: number): Promise<void> {
    await api.delete(`/leads/mapping-templates/${id}`)
  },

  async reassign(leadId: number, assigneeId: number) {
    const res = await api.post(`/leads/${leadId}/reassign`, { assignee_id: assigneeId })
    return res.data
  },

  async triggerAutoAllocate() {
    const res = await api.post('/leads/auto-allocate')
    return res.data as { status: string; allocated: number }
  },

  async getWorkloadStats() {
    const res = await api.get('/leads/workload/stats')
    return res.data.items as WorkloadItem[]
  },

  async getReallocationSuggestions() {
    const res = await api.get('/leads/reallocation-suggestions')
    return res.data as { suggestions: ReallocationSuggestion[]; total_sla_breached: number; total_imbalanced: number }
  },

  async getTelecallerWeights() {
    const res = await api.get('/admin/users/telecallers/weights')
    return res.data as TelecallerWeight[]
  },

  async setAllocationWeight(userId: number, weight: number) {
    const res = await api.patch(`/admin/users/${userId}/allocation-weight`, { weight })
    return res.data as { user_id: number; allocation_weight: number }
  },

  async reassignLead(leadId: number, assigneeId: number) {
    const res = await api.post(`/leads/${leadId}/reassign`, { assignee_id: assigneeId })
    return res.data
  },

  // ── Click-to-call ──────────────────────────────────────────────────────────

  /**
   * Initiate an Exotel click-to-call for a lead.
   * Returns a call_log_id to be used with updateDisposition().
   */
  async initiateCall(leadId: number): Promise<CallInitiateResponse> {
    const res = await api.post(`/leads/${leadId}/calls/initiate`)
    return res.data
  },

  async mobileInitiateCall(
    leadId: number,
    opts?: { direction?: 'outbound' | 'inbound'; started_at?: string; duration_s?: number }
  ): Promise<{ call_log_id: number }> {
    const body = new FormData()
    if (opts?.direction)  body.append('direction', opts.direction)
    if (opts?.started_at) body.append('started_at', opts.started_at)
    if (opts?.duration_s != null) body.append('duration_s', String(opts.duration_s))
    const res = await api.post(`/leads/${leadId}/calls/mobile-initiate`, body)
    return res.data
  },

  async getLeadByMobileHash(number: string): Promise<{ lead_id: number; customer_name: string; status: string } | null> {
    try {
      const res = await api.get('/leads/by-mobile-hash', { params: { number } })
      return res.data
    } catch {
      return null  // 404 = personal/unknown call — caller should skip
    }
  },

  async getCallStatus(callLogId: number): Promise<CallStatusResponse> {
    const res = await api.get(`/calls/${callLogId}/status`)
    return res.data
  },

  /**
   * Record the outcome of a call after it ends.
   */
  async updateDisposition(callLogId: number, data: {
    disposition: string
    remarks?: string
    callback_at?: string
    od_discount_offered?: number
    requires_approval?: boolean
  }): Promise<{ reminder_recommended?: boolean; [key: string]: unknown }> {
    const res = await api.patch(`/calls/${callLogId}`, data)
    return res.data
  },

  /**
   * Log a manual call (no Exotel) — used when Smart Connect is not enabled.
   * Creates a CallLog immediately marked COMPLETED with the given disposition.
   */
  async logManualCall(leadId: number, data: {
    source: CallSource
    disposition: string
    remarks?: string
    callback_at?: string
    od_discount_offered?: number
    requires_approval?: boolean
  }): Promise<{ call_log_id: number; reminder_recommended?: boolean; within_requested_slot?: boolean | null }> {
    const res = await api.post(`/leads/${leadId}/calls/log`, data)
    return res.data
  },

  // ── Quotes ─────────────────────────────────────────────────────────────────

  async listQuotes(leadId: number): Promise<Quote[]> {
    const res = await api.get(`/leads/${leadId}/quotes`)
    return res.data
  },

  async uploadQuote(leadId: number, pdfFile: File, metadata: {
    insurer_name?: string
    policy_type?: string
    premium_amount?: number
    idv?: number
    od_premium?: number
    tp_premium?: number
    zero_depreciation?: boolean
    roadside_assistance?: boolean
    engine_protection?: boolean
    consumables_cover?: boolean
    ncb_protection?: boolean
    key_replacement?: boolean
    deferred?: boolean
    /** Upload-slot index (0-based) — pass this when uploading multiple quotes so
     *  display/WhatsApp order matches the order files were selected, independent
     *  of which parallel request's AI extraction finishes first. */
    sort_order?: number
  }): Promise<Quote> {
    const form = new FormData()
    form.append('pdf_file', pdfFile)
    if (metadata.insurer_name) form.append('insurer_name', metadata.insurer_name)
    if (metadata.policy_type) form.append('policy_type', metadata.policy_type)
    if (metadata.premium_amount != null) form.append('premium_amount', String(metadata.premium_amount))
    if (metadata.idv != null) form.append('idv', String(metadata.idv))
    if (metadata.od_premium != null) form.append('od_premium', String(metadata.od_premium))
    if (metadata.tp_premium != null) form.append('tp_premium', String(metadata.tp_premium))
    form.append('zero_depreciation', String(metadata.zero_depreciation ?? false))
    form.append('roadside_assistance', String(metadata.roadside_assistance ?? false))
    form.append('engine_protection', String(metadata.engine_protection ?? false))
    form.append('consumables_cover', String(metadata.consumables_cover ?? false))
    form.append('ncb_protection', String(metadata.ncb_protection ?? false))
    form.append('key_replacement', String(metadata.key_replacement ?? false))
    form.append('deferred', String(metadata.deferred ?? false))
    if (metadata.sort_order != null) form.append('sort_order', String(metadata.sort_order))
    const res = await api.post(`/leads/${leadId}/quotes`, form, {
      headers: { 'Content-Type': undefined },
    })
    return res.data
  },

  async sendQuotes(leadId: number) {
    const res = await api.post(`/leads/${leadId}/quotes/send`)
    return res.data
  },

  async revealMobile(leadId: number): Promise<{ mobile: string }> {
    const res = await api.post(`/leads/${leadId}/reveal-mobile`)
    return res.data
  },

  async sendPaymentLink(leadId: number, paymentUrl: string, amount: number, qrImage: File) {
    const form = new FormData()
    form.append('payment_url', paymentUrl)
    form.append('amount', String(amount))
    form.append('qr_image', qrImage)
    const res = await api.post(`/leads/${leadId}/send-payment-link`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return res.data
  },

  /**
   * Get a signed 24-hour PDF download token for a quote.
   * Combine with GET /quotes/{id}/pdf?token=... to download.
   */
  async getQuotePdfToken(quoteId: number): Promise<{ token: string; quote_id: number }> {
    const res = await api.get(`/quotes/${quoteId}/pdf-token`)
    return res.data
  },

  async deleteQuote(leadId: number, quoteId: number): Promise<void> {
    await api.delete(`/leads/${leadId}/quotes/${quoteId}`)
  },

  async updateQuote(leadId: number, quoteId: number, data: {
    insurer_name?: string
    policy_type?: string
    premium_amount?: number
    idv?: number
    od_premium?: number
    tp_premium?: number
    zero_depreciation?: boolean
    roadside_assistance?: boolean
    engine_protection?: boolean
    consumables_cover?: boolean
    ncb_protection?: boolean
    key_replacement?: boolean
  }): Promise<Quote> {
    const res = await api.patch(`/leads/${leadId}/quotes/${quoteId}`, data)
    return res.data
  },

  async extractQuote(leadId: number, quoteId: number): Promise<QuoteExtractionResult> {
    const res = await api.post(`/leads/${leadId}/quotes/${quoteId}/extract`)
    return res.data
  },

  async previewQuoteMessage(leadId: number): Promise<QuotePreviewMessage> {
    const res = await api.post(`/leads/${leadId}/quotes/preview-message`)
    return res.data
  },

  async sendQuoteTemplate(leadId: number): Promise<{ status: string; message_preview: string; quote_count: number }> {
    const res = await api.post(`/leads/${leadId}/quotes/send-template`)
    return res.data
  },

  // ── Discount workflow ──────────────────────────────────────────────────────

  async approveDiscount(callId: number) {
    const res = await api.post(`/calls/${callId}/approve-discount`)
    return res.data
  },

  async rejectDiscount(callId: number, reason: string) {
    const res = await api.post(`/calls/${callId}/reject-discount`, null, { params: { reason } })
    return res.data
  },

  async escalateDiscount(callId: number, comment: string) {
    const res = await api.post(`/calls/${callId}/escalate-discount`, null, { params: { comment } })
    return res.data
  },

  async getPendingDiscounts(): Promise<PendingDiscount[]> {
    const res = await api.get('/calls/pending-discounts')
    return res.data
  },

  async getDiscountLimits(): Promise<DiscountLimits> {
    const res = await api.get('/admin/discount-limits')
    return res.data
  },

  async setDiscountLimits(limits: DiscountLimits): Promise<DiscountLimits> {
    const res = await api.patch('/admin/discount-limits', limits)
    return res.data
  },
}

export interface PendingDiscount {
  id: number
  lead_id: number | null
  customer_name: string | null
  vehicle_reg_no: string | null
  vehicle_model: string | null
  tc_name: string | null
  od_discount_offered: number | null
  escalated_to: 'TEAM_LEADER' | 'INSURANCE_HEAD' | null
  escalation_comment: string | null
  escalation_chain: Array<{ role: string; by: string; comment: string; at: string }> | null
  approval_status: string
  created_at: string | null
}

export interface DiscountLimits {
  telecaller: number
  team_leader: number
  insurance_head: number
}
